import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { t } from 'ember-intl';
import { and } from 'ember-truth-helpers';

import Alert from 'umg-ember-backend/components/ui/alert';
import Button from 'umg-ember-backend/components/ui/button';
import stopPropagation from 'umg-ember-backend/helpers/stop-propagation';
import { getBem } from 'umg-ember-backend/utils/get-bem';
import { lookupTranslation } from 'umg-ember-backend/utils/intl';

import Item from './item';
import styles from './styles.module.css';

const bem = getBem(styles);

export default class PageBuilderContentItemsSelectComponent extends Component {
  // Services
  @service intl;

  // Defaults
  @tracked isSelectOpen = false;

  // Getter and setter
  get addContentItemLabel() {
    if (this.onlyOneContentItemType) {
      let firstType = this.firstContentItemTypeOption;

      return this.intl.t(`models.${firstType}`);
    }

    return this.intl.t('page_builder.content_items.select.add');
  }

  get firstContentItemTypeOption() {
    return this.args.contentItemTypeOptions[0];
  }

  get hasContentItemTypeOptions() {
    return this.args.contentItemTypeOptions?.length > 0;
  }

  get isSelectAllowed() {
    return this.args.isSelectAllowed ?? true;
  }

  get noItemsMessage() {
    if (this.onlyOneContentItemType) {
      let firstType = this.firstContentItemTypeOption;

      const lookups = [
        `page_builder.content_items.select.no_items.${firstType}`,
        'page_builder.content_items.select.no_items.content',
      ];

      return lookupTranslation.call(this, lookups);
    }

    return this.intl.t('page_builder.content_items.select.no_items.content');
  }

  get onlyOneContentItemType() {
    return this.args.contentItemTypeOptions?.length == 1;
  }

  get showNoItemsMessage() {
    return !this.args.siblings?.length;
  }

  // Functions
  selectContentItem = () => {
    if (this.onlyOneContentItemType) {
      this.args.onAddContentItem(this.firstContentItemTypeOption);
    } else {
      this.toggleSelect();
    }
  };

  toggleSelect = () => {
    this.isSelectOpen = !this.isSelectOpen;
  };

  onSelect = (contentItemType) => {
    this.args.onAddContentItem(contentItemType);
    this.toggleSelect();
  };

  // Template
  <template>
    {{#if (and this.isSelectAllowed this.hasContentItemTypeOptions)}}
      <div
        class={{(bem)}}
        data-test-page-builder-content-items-select
        ...attributes
      >

        {{#if @style}}
          {{#unless @isDisabled}}
            <div
              class={{bem 'inline-button' (hash style=@style)}}
              role='button'
              tabindex='0'
              {{on 'click' (stopPropagation this.selectContentItem)}}
            >
              <div class={{bem 'plus-icon'}}>
                +
              </div>
            </div>
          {{/unless}}
        {{else}}
          <Button
            class={{bem 'toggle'}}
            @style='inline'
            @disabled={{@isDisabled}}
            @icon='add'
            @label={{this.addContentItemLabel}}
            data-test-page-builder-content-items-select-toggle
            {{on 'click' (stopPropagation this.selectContentItem)}}
          />
          {{#if this.showNoItemsMessage}}
            <div
              class={{bem 'no-items'}}
              data-test-page-builder-content-items-select-no-items
            >

              {{this.noItemsMessage}}
            </div>
          {{/if}}
        {{/if}}

        <Alert
          @isOpen={{this.isSelectOpen}}
          @onClose={{this.toggleSelect}}
          @title={{t 'page_builder.content_items.select.headline'}}
        >
          <div class={{bem 'list'}}>
            {{#each @contentItemTypeOptions as |contentItemType|}}
              <Item
                @contentItemType={{contentItemType}}
                @siblings={{@siblings}}
                @onSelect={{this.onSelect}}
              />
            {{/each}}
          </div>
        </Alert>
      </div>
    {{/if}}
  </template>
}
